/**
 * 编辑态 mention 边界检测（轻量模块，无 editorDom 依赖）
 * 供 useMentionTrigger 等共享层使用，避免 caretUtils → editorDom 循环依赖导致导出未就绪。
 */
import type { IInlineMentionRange } from '../utils/inlineMentionRanges';
import { serializeMentionBody } from './mentionTokenDom';
import type { TMentionEditorKind } from './types';

const MENTION_KIND_ATTR = 'data-mention-kind';
const MENTION_BODY_ATTR = 'data-mention-body';
const MENTION_KEY_ATTR = 'data-mention-key';
const MENTION_TOKEN_CLASS = 'dba-mention-token';

function stripZeroWidthChars(text: string): string {
  return text.replace(/\u200b/g, '');
}

function getTextSerializedLength(text: string): number {
  return stripZeroWidthChars(text).length;
}

function getMentionKind(node: HTMLElement): TMentionEditorKind | null {
  const kind = node.getAttribute(MENTION_KIND_ATTR);
  if (kind === 'knowledge' || kind === 'skill') return kind;
  return null;
}

function isMentionToken(node: Node | null): boolean {
  if (!(node instanceof HTMLElement)) return false;
  const kind = node.getAttribute(MENTION_KIND_ATTR);
  return (
    node.classList.contains(MENTION_TOKEN_CLASS)
    && (kind === 'knowledge' || kind === 'skill')
    && node.hasAttribute(MENTION_BODY_ATTR)
  );
}

/** 编辑态：遍历 DOM 中真实 mention token 的序列化偏移区间 */
export function collectDomMentionRanges(
  root: HTMLElement,
): IInlineMentionRange[] {
  const ranges: IInlineMentionRange[] = [];
  let offset = 0;

  const walk = (node: Node): void => {
    if (node.nodeType === Node.TEXT_NODE) {
      offset += getTextSerializedLength(node.textContent || '');
      return;
    }
    if (!(node instanceof HTMLElement)) return;

    if (isMentionToken(node)) {
      const kind = getMentionKind(node)!;
      const body = node.getAttribute(MENTION_BODY_ATTR) || '';
      const mentionKey = node.getAttribute(MENTION_KEY_ATTR) || '';
      const label = serializeMentionBody(body, kind);
      ranges.push({
        key: mentionKey,
        kind,
        start: offset,
        end: offset + label.length,
        label,
        ids: [],
      });
      offset += label.length;
      return;
    }
    if (node.tagName === 'BR') {
      offset += 1;
      return;
    }
    node.childNodes.forEach(walk);
  };

  root.childNodes.forEach(walk);
  return ranges;
}

/** 编辑态：某偏移是否落在已有 mention token 内（用于 `/` `@` 触发判断） */
export function isOffsetInsideDomMention(
  root: HTMLElement,
  offset: number,
): boolean {
  return collectDomMentionRanges(root).some(
    (range) => offset >= range.start && offset < range.end,
  );
}
